import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import type { HomeHeroBanner as Value } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { getLinkUrl, originalUrl } from "../../utils/wagtail";

import heroStyles from "../../styles/composable/hero.module.scss";
import valuePropsStyles from "../../styles/composable/value-props.module.scss";
import styles from "./index.module.scss";

type IProps = Immutable<{
    value: Value;
}>;

/**
 * Faithful JSX transcription of the legacy sealy
 * ``components/hero_banners/home_hero_banner.html`` (#33285).
 *
 * Two separate regions, both gated on ``self.promotion.image``: the
 * ``hero__badge-link`` inside ``section.hero``, and the trailing
 * ``section.offer-inline`` AFTER it -- hence the fragment return. The
 * literal ``alt="…"`` on the badge img is preserved exactly as legacy
 * emitted it.
 */
export const HomeHeroBanner = (props: IProps): React.JSX.Element => {
    const value = props.value;
    const promotion = value.promotion;
    return (
        <>
            <section className="hero">
                <picture>
                    <source
                        srcSet={originalUrl(value.mobile_image)}
                        media="(max-width: 768px)"
                    />
                    <img
                        className={heroStyles.heroBackground}
                        srcSet={originalUrl(value.image)}
                        alt={value.image?.default_alt_text ?? ""}
                    />
                </picture>

                {promotion?.image && (
                    <a
                        href={getLinkUrl(promotion.link)}
                        className={heroStyles.heroBadgeLink}
                        title={promotion.link?.html_title ?? ""}
                    >
                        <img
                            className="hero__badge img-responsive"
                            srcSet={originalUrl(promotion.image)}
                            alt="…"
                        />
                    </a>
                )}

                <div
                    className={concatClassNames([
                        heroStyles.heroContent,
                        heroStyles.heroContentHome,
                        "container",
                    ])}
                >
                    {value.logo && (
                        <svg
                            className={concatClassNames([
                                heroStyles.heroLogo,
                                "logo-pptech",
                            ])}
                        >
                            <use xlinkHref="#logo-pptech" />
                        </svg>
                    )}
                    <h1 className={heroStyles.heroHeadline}>{value.title}</h1>
                    <a
                        className={concatClassNames([heroStyles.heroCta, "h4"])}
                        href={getLinkUrl(value.link)}
                        title={value.link?.html_title ?? ""}
                    >
                        {value.link?.display_name}
                    </a>
                </div>

                <div
                    className={concatClassNames([
                        valuePropsStyles.valueProps,
                        "container",
                    ])}
                >
                    <div className={valuePropsStyles.valuePropsItem}>
                        <figure className={valuePropsStyles.valuePropsIcon}>
                            <svg className="icon-support">
                                <title>{t`Mattress Icon`}</title>
                                <use xlinkHref="#icon-support" />
                            </svg>
                        </figure>
                        <div className={valuePropsStyles.valuePropsCopy}>
                            <h4 className={valuePropsStyles.valuePropsHeadline}>
                                {t`Posturepedic Technology`}
                            </h4>
                            <p className={valuePropsStyles.valuePropsSubtext}>
                                {t`Exclusive to Sealy`}
                            </p>
                        </div>
                    </div>

                    <div className={valuePropsStyles.valuePropsItem}>
                        <figure className={valuePropsStyles.valuePropsIcon}>
                            <svg className="icon-support">
                                <title>{t`Sealy Certified Icon`}</title>
                                <use xlinkHref="#icon-certified" />
                            </svg>
                        </figure>
                        <div className={valuePropsStyles.valuePropsCopy}>
                            <h4 className={valuePropsStyles.valuePropsHeadline}>
                                {t`Exceptional Warranty`}
                            </h4>
                            <p className={valuePropsStyles.valuePropsSubtext}>
                                {t`Up to 10 years`}
                            </p>
                        </div>
                    </div>

                    <div className={valuePropsStyles.valuePropsItem}>
                        <figure className={valuePropsStyles.valuePropsIcon}>
                            <svg className="icon-support">
                                <title>{t`Heart Icon`}</title>
                                <use xlinkHref="#icon-love" />
                            </svg>
                        </figure>
                        <div className={valuePropsStyles.valuePropsCopy}>
                            <h4 className={valuePropsStyles.valuePropsHeadline}>
                                {t`Over 100 Million Sold`}
                            </h4>
                            <p className={valuePropsStyles.valuePropsSubtext}>
                                {t`A history of 130 years`}
                            </p>
                        </div>
                    </div>

                    <div className={valuePropsStyles.valuePropsItem}>
                        <figure className={valuePropsStyles.valuePropsIcon}>
                            <svg className="icon-support">
                                <title>{t`USA Flag Icon`}</title>
                                <use xlinkHref="#icon-usa" />
                            </svg>
                        </figure>
                        <div className={valuePropsStyles.valuePropsCopy}>
                            <h4 className={valuePropsStyles.valuePropsHeadline}>
                                {t`Made in the USA`}
                            </h4>
                            <p className={valuePropsStyles.valuePropsSubtext}>
                                {t`American made & manufactured`}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {promotion?.image && (
                <section className={styles.offerInline}>
                    <h3 className={styles.offerInlineLeadIn}>
                        {promotion.title}
                    </h3>
                    <h2 className={styles.offerInlineHeadline}>
                        {promotion.subtitle}
                    </h2>
                    <h5 className={styles.offerInlineDate}>
                        {promotion.dates}
                    </h5>
                    <a
                        href={getLinkUrl(promotion.link)}
                        className={styles.offerInlineCta}
                        title={promotion.link?.html_title ?? ""}
                    >
                        {promotion.link?.display_name}
                    </a>
                </section>
            )}
        </>
    );
};
